import {
  EmployeeCard,
  EmployeeCardSkeleton,
} from "@/components/company/employee-card";
import { CompanyRefusalMessage } from "@/components/company/company-run-alerts";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useEmployeeSearch } from "@/hooks/useEmployeeSearch";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey } from "@/lib/company-refusal";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import {
  BadgeInfoIcon,
  ExternalLinkIcon,
  Loader2Icon,
  SearchIcon,
  SparklesIcon,
} from "lucide-react";
import { RiLinkedinFill } from "react-icons/ri";

type CompanyMobileEmployeeSearchSheetProps = {
  readonly company: CompanyDTO;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
};

export default function CompanyMobileEmployeeSearchSheet({
  company,
  open,
  onOpenChange,
}: CompanyMobileEmployeeSearchSheetProps) {
  const { t } = useTranslation();
  const arePeopleVisible = useArePeopleVisible();

  // A team whose direct contact is cut has no key contacts to search (#757).
  if (!arePeopleVisible) return null;

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange}>
      <SheetTitle className="sr-only">
        {t("company.mobile.employees.openSearchCta")}
      </SheetTitle>
      <SheetDescription className="sr-only">
        {t("company.employees.searchQuery.description")}
      </SheetDescription>
      {open && <SheetBody company={company} />}
    </MobileSheet>
  );
}

function LinkedInPeopleCta({
  linkedInUrl,
  className,
}: {
  readonly linkedInUrl: string;
  readonly className?: string;
}) {
  const { t } = useTranslation();
  return (
    <a
      href={`${linkedInUrl}/people`}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold transition active:brightness-90",
        className,
      )}
    >
      <RiLinkedinFill className="w-4 h-4" />
      {t("company.employees.viewOnLinkedIn")}
      <ExternalLinkIcon className="w-3.5 h-3.5" />
    </a>
  );
}

function SheetBody({ company }: { company: CompanyDTO }) {
  const { t } = useTranslation();
  const search = useEmployeeSearch({
    company,
    smallCompanyDetection: { mode: "exact", threshold: 10 },
  });

  const linkedInUrl = company.socialNetworks?.linkedin?.url ?? null;

  // The team's daily reads or a throttle, said as such: the hook leaves a
  // refusal to the screen that shows the search.
  const refusal = search.error ? companyRefusalKey(search.error) : null;

  const notAllShownMessage = search.targetMatch
    ? t(`company.employees.notAllEmployeesAreShown.${search.targetMatch}`)
    : null;

  if (search.isSmallCompany) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-5 pt-3 pb-4 shrink-0">
          <h2 className="text-2xl font-semibold tracking-tight">
            {t("company.employees.suggested")}
          </h2>
          {linkedInUrl && (
            <LinkedInPeopleCta
              linkedInUrl={linkedInUrl}
              className="mt-4 h-11 shadow-sm active:scale-[0.99] transition"
            />
          )}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-5 pb-4">
          <EmployeeStack
            isLoading={search.isSearching || search.isSearchQueryLoading}
            employees={search.employees}
            companyId={company.id}
            onEmployeeChanged={search.handleEmployeeChanged}
          />
        </div>
      </div>
    );
  }

  const showResults =
    search.hasSearched && (search.isSearching || search.employees.length > 0);
  const isAIDisabled =
    search.isGeneratingQuery ||
    search.isSearching ||
    search.isSearchQueryLoading;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-5 pt-3 pb-3 shrink-0">
        <h2 className="text-2xl font-semibold tracking-tight pr-8">
          {t("company.employees.searchQuery.title")}
        </h2>

        <button
          type="button"
          onClick={search.handleGenerateQuery}
          disabled={isAIDisabled}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary/10 hover:bg-primary/15 active:bg-primary/20 disabled:opacity-60 transition pl-2 pr-3 py-1.5 max-w-full"
          aria-label={t("company.employees.searchQuery.generateTarget")}
        >
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 shrink-0">
            {search.isGeneratingQuery ? (
              <Loader2Icon className="w-3 h-3 text-primary animate-spin" />
            ) : (
              <SparklesIcon className="w-3 h-3 text-primary" />
            )}
          </div>
          <div className="flex flex-col items-start min-w-0">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-primary leading-none">
              {t("company.employees.searchQuery.generateTarget")}
            </span>
            <span className="text-[11px] text-primary/70 leading-tight truncate">
              {t("company.employees.searchQuery.generateTargetHint")}
            </span>
          </div>
        </button>

        <div className="mt-3 rounded-2xl bg-muted/40 ring-1 ring-border/60 overflow-hidden">
          <Input
            value={search.searchQuery}
            onChange={(e) => search.setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !search.isButtonDisabled()
              ) {
                e.preventDefault();
                search.handleButtonClick();
              }
            }}
            placeholder={t("company.employees.searchQuery.placeholder")}
            className="h-11 w-full bg-transparent border-0 shadow-none ring-0 focus-visible:ring-0 px-4 text-base placeholder:text-muted-foreground/50"
          />
        </div>

        {search.getDisabledTooltip() && (
          <p className="mt-2 text-xs text-muted-foreground">
            {search.getDisabledTooltip()}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {refusal && <CompanyRefusalMessage refusal={refusal} className="py-8" />}

        {!refusal && !search.hasSearched && !search.isSearchQueryLoading && (
          <div className="flex flex-col items-center justify-center py-12 px-6 text-center text-muted-foreground">
            <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center mb-3">
              <SearchIcon className="w-5 h-5 text-muted-foreground/70" />
            </div>
            <p className="text-sm max-w-[260px]">
              {t("company.employees.searchQuery.emptyState.description")}
            </p>
          </div>
        )}

        {showResults && (
          <div className="px-5 py-3 flex flex-col gap-3">
            {notAllShownMessage && (
              <Alert className="flex flex-col gap-3 rounded-2xl [&>svg]:hidden">
                <div className="flex items-center gap-2">
                  <BadgeInfoIcon className="w-5 h-5 shrink-0" />
                  <p className="text-sm">{notAllShownMessage}</p>
                </div>
                {linkedInUrl && (
                  <LinkedInPeopleCta linkedInUrl={linkedInUrl} className="h-10" />
                )}
              </Alert>
            )}
            <EmployeeStack
              isLoading={search.isSearching && search.employees.length === 0}
              employees={search.employees}
              companyId={company.id}
              onEmployeeChanged={search.handleEmployeeChanged}
            />
            {search.maxPagesReached && (
              <p className="text-xs text-muted-foreground text-center py-2">
                {t("company.employees.searchQuery.refineSearch.description")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border/60 bg-background/85 backdrop-blur-md px-4 pt-3 pb-3">
        <Button
          onClick={search.handleButtonClick}
          disabled={search.isButtonDisabled()}
          className="w-full h-12 rounded-full text-base font-semibold shadow-sm active:brightness-90"
        >
          {search.isSearching ? (
            <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <SearchIcon className="w-4 h-4 mr-2" />
          )}
          {search.getButtonText()}
        </Button>
      </div>
    </div>
  );
}

function EmployeeStack({
  isLoading,
  employees,
  companyId,
  onEmployeeChanged,
}: {
  isLoading: boolean;
  employees: ReturnType<typeof useEmployeeSearch>["employees"];
  companyId: string;
  onEmployeeChanged: ReturnType<
    typeof useEmployeeSearch
  >["handleEmployeeChanged"];
}) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <EmployeeCardSkeleton key={i} />
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {employees.map((employee) => (
        <EmployeeCard
          key={employee.id}
          employee={employee}
          companyId={companyId}
          onEmployeeChanged={onEmployeeChanged}
          compact
        />
      ))}
    </div>
  );
}
